//Frontend/src/unicodex_ui/pages/InventoryDashboardPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ManufacturerNav from "../components/ManufacturerNav";
import portalInventoryApi from "../services/portalInventoryApi";
import { clearPortalSession, getStoredPermissions, getStoredPortalUser } from "../services/authApi";
import "../styles/PortalBusiness.css";

function InventoryDashboardPage() {
  const navigate = useNavigate();
  const perms = getStoredPermissions();
  const user = getStoredPortalUser();

  const links = useMemo(
    () => [
      { to: "/portal/inventory", label: "Inventory" },
      { to: "/portal/purchasing", label: "Purchasing", perm: "purchasing.po.read" },
      { to: "/portal/ops", label: "Ops" },
    ],
    []
  );

  const canAdjust = perms.includes("inventory.stock.adjust");
  const canReceive = perms.includes("inventory.stock.receive_manufactured");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");

  const [stockLevels, setStockLevels] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);

  // adjust form
  const [adjProductId, setAdjProductId] = useState("");
  const [adjBatchId, setAdjBatchId] = useState("");
  const [adjQty, setAdjQty] = useState(0);
  const [adjReason, setAdjReason] = useState("correction");

  // receive form
  const [batchQuery, setBatchQuery] = useState("");
  const [receivableBatches, setReceivableBatches] = useState([]);
  const [receivableTotal, setReceivableTotal] = useState(0);
  const [selectedBatchId, setSelectedBatchId] = useState("");

  const selectedBatch = useMemo(
    () => receivableBatches.find((b) => String(b.id) === String(selectedBatchId)) || null,
    [receivableBatches, selectedBatchId]
  );

  function signOut() {
    clearPortalSession();
    navigate("/portal/login", { replace: true });
  }

  async function loadBranches() {
    const bRes = await portalInventoryApi.listBranches();
    if (bRes?.ok !== true) throw new Error("branches_failed");
    const b = Array.isArray(bRes.branches) ? bRes.branches : [];
    setBranches(b);
    if (!branchId && b.length) setBranchId(String(b[0].id));
  }

  async function loadLocations(bid) {
    if (!bid) {
      setLocations([]);
      setLocationId("");
      return;
    }
    const lRes = await portalInventoryApi.listLocations(Number(bid));
    if (lRes?.ok !== true) throw new Error("locations_failed");
    const l = Array.isArray(lRes.locations) ? lRes.locations : [];
    setLocations(l);
    if (!locationId && l.length) setLocationId(String(l[0].id));
  }

  async function loadData(bid, lid) {
    const params = { branchId: Number(bid) };
    if (lid) params.locationId = Number(lid);

    const [slRes, smRes] = await Promise.all([
      portalInventoryApi.listStockLevels({ ...params, limit: 100, offset: 0 }),
      portalInventoryApi.listStockMovements({ ...params, limit: 100, offset: 0 }),
    ]);

    if (slRes?.ok !== true) throw new Error("stock_levels_failed");
    if (smRes?.ok !== true) throw new Error("stock_movements_failed");

    setStockLevels(Array.isArray(slRes.stockLevels) ? slRes.stockLevels : []);
    setStockMovements(Array.isArray(smRes.stockMovements) ? smRes.stockMovements : []);
  }

  async function loadReceivableBatches(q) {
    const params = { limit: 50, offset: 0 };
    if (q && String(q).trim()) params.q = String(q).trim();
    const res = await portalInventoryApi.listReceivableBatches(params);
    if (res?.ok !== true) throw new Error("receivable_batches_failed");
    setReceivableBatches(Array.isArray(res.rows) ? res.rows : []);
    setReceivableTotal(Number(res.total || 0));
  }

  async function reloadAll() {
    setLoading(true);
    setError("");
    setOkMsg("");
    try {
      await loadBranches();
      if (canReceive) {
        try {
          await loadReceivableBatches("");
        } catch (e) {
          // non-fatal: receive UI can still render but list may be empty
          console.error("[InventoryDashboard] receivable batches error:", e);
        }
      }
    } catch (e) {
      console.error("[InventoryDashboard] branches error:", e);
      setError("Unable to load branches.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reloadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search for receivable batches
  useEffect(() => {
    if (!canReceive) return;
    const t = setTimeout(async () => {
      try {
        await loadReceivableBatches(batchQuery);
        // if current selection disappeared, clear it
        if (
          selectedBatchId &&
          !receivableBatches.some((r) => String(r.id) === String(selectedBatchId))
        ) {
          setSelectedBatchId("");
        }
      } catch (e) {
        // non-fatal
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchQuery]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setError("");
        await loadLocations(branchId);
      } catch (e) {
        console.error("[InventoryDashboard] locations error:", e);
        if (alive) setError("Unable to load locations.");
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!branchId) return;
      try {
        setLoading(true);
        setError("");
        await loadData(branchId, locationId);
      } catch (e) {
        console.error("[InventoryDashboard] load error:", e);
        if (alive) setError("Unable to load stock data.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, locationId]);

  async function onAdjust(e) {
    e.preventDefault();
    setError("");
    setOkMsg("");

    if (!locationId) {
      setError("Location is required.");
      return;
    }
    if (!adjProductId || Number(adjProductId) <= 0) {
      setError("Product ID is required.");
      return;
    }

    try {
      const payload = {
        productId: Number(adjProductId),
        batchId: adjBatchId ? Number(adjBatchId) : undefined,
        locationId: Number(locationId),
        quantity: Number(adjQty),
        reason: adjReason,
      };

      const res = await portalInventoryApi.adjustStock(payload);
      if (res?.ok !== true) throw new Error("adjust_failed");
      setOkMsg("Stock adjusted.");
      await loadData(branchId, locationId);
    } catch (e) {
      console.error("[InventoryDashboard] adjust error:", e);
      setError("Unable to adjust stock.");
    }
  }

  async function onReceive(e) {
    e.preventDefault();
    setError("");
    setOkMsg("");

    if (!locationId) {
      setError("Location is required.");
      return;
    }
    if (!selectedBatchId || Number(selectedBatchId) <= 0) {
      setError("Batch is required.");
      return;
    }

    try {
      const payload = {
        batchId: Number(selectedBatchId),
        locationId: Number(locationId),
      };

      const res = await portalInventoryApi.receiveManufactured(payload);
      if (res?.ok !== true) throw new Error("receive_failed");
      setOkMsg("Manufactured stock received.");
      await loadData(branchId, locationId);

      // refresh receivable list so the received batch disappears
      if (canReceive) {
        await loadReceivableBatches(batchQuery);
        setSelectedBatchId("");
      }
    } catch (e) {
      console.error("[InventoryDashboard] receive error:", e);
      setError("Unable to receive manufactured stock.");
    }
  }

  return (
    <section className="ucx-section ucx-portal-page">
      <div className="ucx-container">
        <ManufacturerNav
          title="Business Portal"
          subtitle={user?.email || "Signed in"}
          links={links}
          onSignOut={signOut}
        />

        <div className="ucx-portal-panel">
          <div className="ucx-portal-head">
            <div>
              <h1 className="ucx-portal-title">Inventory</h1>
              <p className="ucx-portal-sub">Stock levels and movement history.</p>
            </div>

            <div className="ucx-portal-actions">
              <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => loadData(branchId, locationId)} disabled={!branchId || loading}>
                Refresh
              </button>
              {perms.includes("purchasing.po.read") && (
                <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => navigate("/portal/purchasing")}>
                  Purchasing
                </button>
              )}
            </div>
          </div>

          {error && <div className="ucx-portal-error">{error}</div>}
          {okMsg && <div className="ucx-portal-success">{okMsg}</div>}

          <div className="ucx-portal-form">
            <div className="ucx-portal-field">
              <label>Branch</label>
              <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div className="ucx-portal-field">
              <label>Location</label>
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                <option value="">All</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>

            <div className="ucx-portal-field">
              <label>&nbsp;</label>
              <button className="ucx-btn ucx-btn-outline" type="button" onClick={reloadAll} disabled={loading}>
                Reload branches
              </button>
            </div>
          </div>

          {loading ? (
            <div className="ucx-muted">Loading…</div>
          ) : (
            <>
              {(canAdjust || canReceive) && (
                <div className="ucx-portal-grid">
                  {canAdjust && (
                    <div className="ucx-portal-card">
                      <div className="ucx-portal-card-label">Adjust stock</div>
                      <form onSubmit={onAdjust}>
                        <div className="ucx-portal-form" style={{ gridTemplateColumns: "minmax(0,1fr)" }}>
                          <div className="ucx-portal-field">
                            <label>Product ID *</label>
                            <input value={adjProductId} onChange={(e) => setAdjProductId(e.target.value)} placeholder="e.g. 1" />
                          </div>
                          <div className="ucx-portal-field">
                            <label>Batch ID (optional)</label>
                            <input value={adjBatchId} onChange={(e) => setAdjBatchId(e.target.value)} placeholder="e.g. 10" />
                          </div>
                          <div className="ucx-portal-field">
                            <label>Quantity (+/-)</label>
                            <input type="number" value={adjQty} onChange={(e) => setAdjQty(e.target.value)} />
                          </div>
                          <div className="ucx-portal-field">
                            <label>Reason</label>
                            <select value={adjReason} onChange={(e) => setAdjReason(e.target.value)}>
                              <option value="correction">correction</option>
                              <option value="damage">damage</option>
                              <option value="shrinkage">shrinkage</option>
                              <option value="transfer">transfer</option>
                            </select>
                          </div>
                        </div>
                        <button className="ucx-btn ucx-btn-primary" type="submit" disabled={!locationId}>
                          Adjust
                        </button>
                      </form>
                    </div>
                  )}

                  {canReceive && (
                    <div className="ucx-portal-card">
                      <div className="ucx-portal-card-label">Receive manufactured stock</div>
                      <form onSubmit={onReceive}>
                        <div className="ucx-portal-form" style={{ gridTemplateColumns: "minmax(0,1fr)" }}>
                          <div className="ucx-portal-field">
                            <label>Search batch</label>
                            <input
                              value={batchQuery}
                              onChange={(e) => setBatchQuery(e.target.value)}
                              placeholder="Search by UCX batch, product, SKU, manufacturer…"
                            />
                            <div className="ucx-small" style={{ marginTop: 6, opacity: 0.8 }}>
                              Showing {receivableBatches.length} of {receivableTotal}
                            </div>
                          </div>
                          <div className="ucx-portal-field">
                            <label>Batch (UCX) *</label>
                            <select value={selectedBatchId} onChange={(e) => setSelectedBatchId(e.target.value)}>
                              <option value="">Select a batch…</option>
                              {receivableBatches.map((b) => (
                                <option key={b.id} value={b.id}>
                                  {(b.ucx_batch || `BATCH-${b.id}`)} — {b.product_name || b.product_id} — {b.manufacturer_name || b.manufacturer_prefix || "—"} — size {b.batch_size}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="ucx-portal-form" style={{ gridTemplateColumns: "minmax(0,1fr)" }}>
                          <div className="ucx-portal-field">
                            <label>Manufacturer</label>
                            <input value={selectedBatch ? (selectedBatch.manufacturer_name || selectedBatch.manufacturer_prefix || "—") : ""} disabled />
                          </div>
                          <div className="ucx-portal-field">
                            <label>Product</label>
                            <input value={selectedBatch ? (selectedBatch.product_name || selectedBatch.product_id || "—") : ""} disabled />
                          </div>
                          <div className="ucx-portal-field">
                            <label>Batch size (fixed)</label>
                            <input value={selectedBatch ? String(selectedBatch.batch_size || "") : ""} disabled />
                          </div>
                        </div>
                        <button className="ucx-btn ucx-btn-primary" type="submit" disabled={!locationId}>
                          Receive
                        </button>
                      </form>
                    </div>
                  )}
                </div>
              )}

              <h3 className="ucx-portal-title" style={{ marginTop: 18 }}>Stock levels</h3>
              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Batch</th>
                      <th>Location</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockLevels.map((r, idx) => (
                      <tr key={r.id || idx}>
                        <td>{r.product_id || r.productId || "—"}</td>
                        <td>{r.batch_id || r.batchId || "—"}</td>
                        <td>{r.location_name || r.locationId || "—"}</td>
                        <td>{r.quantity ?? "—"}</td>
                      </tr>
                    ))}
                    {!stockLevels.length && (
                      <tr><td colSpan="4" className="ucx-muted">No stock levels found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="ucx-portal-title" style={{ marginTop: 18 }}>Movement history</h3>
              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Product</th>
                      <th>Batch</th>
                      <th>Location</th>
                      <th>Qty</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockMovements.map((m, idx) => (
                      <tr key={m.id || idx}>
                        <td>{m.created_at ? String(m.created_at).replace("T", " ").slice(0, 19) : "—"}</td>
                        <td>{m.product_id || m.productId || "—"}</td>
                        <td>{m.batch_id || m.batchId || "—"}</td>
                        <td>{m.location_name || m.locationId || "—"}</td>
                        <td>{m.quantity ?? "—"}</td>
                        <td>{m.reason || "—"}</td>
                      </tr>
                    ))}
                    {!stockMovements.length && (
                      <tr><td colSpan="6" className="ucx-muted">No movements found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default InventoryDashboardPage;
